import { useContext, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { isPast } from 'date-fns';

import { Box, Button, Heading, Page, Paragraph, Popover, Tag, Text } from '@zillow/constellation-10';
import { AdproductsConstants } from '@zg-rentals/rental-revenue-components';
import { PartialMessage } from '@zg-rentals/backend-styled-components';

import { PRODUCT_TYPE_NAME_TO_ID } from '../../constants/FreemiumConstants';

import { RMWContext } from '../../providers/RMWContext';

import { AppSelectors } from '../../selectors';
import { useSelector } from '../../selectors/useSelector';

import { getTagTypeByKey, STATUS_KEY } from '../../utils/statusTagUtils';

import { LAUNCHED_FROM_OPTIONS } from '../../components/Modal/DeactivationModal/constants';

import useVisibility from '../../hooks/useVisibility';

import DeactivationModal from '../../components/Modal/DeactivationModal';

import FeedListingDeactivateModal from '../../components/Modal/FeedListingDeactivateModal';

import MultifamilyDeactivationModal from '../../components/Modal/MultifamilyDeactivationModal';

import WaitlistModal from '../../components/Modal/WaitlistModal';

import { getCTAButtonArgs } from './utils';

import AdditionalInformation from './components/AdditionalInformation';
import ErrorMessage from './components/ErrorMessage';
import FeedConnectAdditionalInformation from './components/FeedConnectAdditionalInformation';
import LearnMore from './components/LearnMore';

import type { ReactNode, RefObject } from 'react';
import { flexContainerStyles, middleFlexStyles, pageContentStyles, stickyListingFooterContainerStyles } from './styles';

const { makeIsScreenMobile, makeIsWizardView } = AppSelectors;

type Props = {
  onPublishClick: (args: { shouldAutoActivate: boolean }) => void;
  isPublishLoading?: boolean;
  isPublishedDisabled?: boolean;
  ctaRef?: RefObject<HTMLButtonElement>;
};

const waitlistEligibleProductTypes = [
  AdproductsConstants.ProductCodes.PAY_PER_LEASE,
  AdproductsConstants.ProductCodes.FLAT_FEE,
];

const ConnectedListingFooter = ({
  onPublishClick,
  isPublishLoading = false,
  isPublishedDisabled = false,
  ctaRef,
}: Props) => {
  const { isInMobileWebView } = useContext(RMWContext);

  const {
    features,
    isPostingPathAdmin,
    currentProperty: {
      adproduct,
      active,
      housingConnectorStatus,
      isActivationBlocked,
      isFeed,
      isPackageProduct,
      isPaidMultifamily,
      isWaitlisted,
      productType,
      singleFamilyContract,
      selfServeOnboardingStatus: { isSelfServeOnboardingInProgress, isUpdateRestricted },
      syndication,
      syndication: {
        overview: { displayStatus },
      },
      addressRestrictions: { paidListingExists, hasRestrictionErrorMessage },
      freemium: {
        activeProduct: { type: freemiumActiveProductType, expirationTimestamp },
        isEnabled: isFreemiumEnabled,
      },
      rentalProtection: { isEligible: isEligibleForRentGuarantee, isEnabled: isEnabledForRentGuarantee },
      updateActiveStatus: { partialMessages = [], isAllowed: isUpdateActiveStatusAllowed },
    },
    isScreenMobile,
    isWizardView,
  } = useSelector(
    (state) => ({
      currentProperty: state.properties.currentProperty,
      features: state.user.features,
      isPostingPathAdmin: state.user.isPostingPathAdmin,
      isScreenMobile: makeIsScreenMobile(state),
      isWizardView: makeIsWizardView(state),
    }),
    shallowEqual,
  );

  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [displayDeactivationModal, setDisplayDeactivateModal] = useState(false);
  const [showMultifamilyDeactivationModal, setShowMultifamilyDeactivationModal] = useState(false);
  const [showFeedListingDeactivateModal, setShowFeedListingDeactivateModal] = useState(false);

  const [isVisible, isVisibleRef] = useVisibility(-50, 200);

  const isNotActiveAndUpdateActiveStatusNotAllowed = !active && !isUpdateActiveStatusAllowed;
  const isActiveAndUpdateActiveStatusNotAllowed = active && !isUpdateActiveStatusAllowed;
  const isDuplicatePaidListingPosted = hasRestrictionErrorMessage && paidListingExists;

  const disablePublishIfDuplicatePaidListingPosted = !isFeed && !active && isDuplicatePaidListingPosted;

  const renderPricingContent = !isActivationBlocked && !disablePublishIfDuplicatePaidListingPosted;
  const showFeedConnectRate = singleFamilyContract.isRequired;

  const isActiveProductExpired = isPast(new Date(expirationTimestamp));
  const shouldAutoActivate = freemiumActiveProductType === PRODUCT_TYPE_NAME_TO_ID.PREMIUM && !isActiveProductExpired;

  const handleDeactivateClick = () => {
    if (isActiveAndUpdateActiveStatusNotAllowed) {
      // do not fall through. we will display a popper
      return;
    }

    if (isFeed) {
      return setShowFeedListingDeactivateModal(true);
    }

    setDisplayDeactivateModal(true);
  };

  const handleCTAClick = () => {
    if (active) {
      if (isPackageProduct || waitlistEligibleProductTypes.includes(productType) || adproduct.isUnpaid) {
        setShowWaitlistModal(true);
      } else if (isPaidMultifamily && !isPostingPathAdmin) {
        setShowMultifamilyDeactivationModal(true);
      } else {
        handleDeactivateClick();
      }
    } else {
      onPublishClick({ shouldAutoActivate });
    }
  };

  const withUnableToDeactivatePopper = (component: ReactNode) => {
    if (!component) {
      return null;
    }

    return (
      <Popover
        trigger={component}
        tone="info"
        header={<Heading level={6}>Unable to deactivate</Heading>}
        body={
          <Paragraph>
            <PartialMessage partialMessages={partialMessages} />
          </Paragraph>
        }
      />
    );
  };

  const renderButton = ({ textLabel, isButtonDisabled }: { textLabel: string; isButtonDisabled: boolean }) => {
    const isCTADeactivate = textLabel.toLowerCase().includes('deactivate');
    const buttonType = !isCTADeactivate || (isCTADeactivate && isUpdateActiveStatusAllowed) ? 'primary' : 'tertiary';

    return (
      <Button
        ref={ctaRef}
        data-test="StatusButton"
        tone={buttonType === 'primary' ? 'brand' : 'neutral'}
        emphasis={buttonType === 'primary' ? 'filled' : 'outlined'}
        disabled={isButtonDisabled}
        onClick={handleCTAClick}
        loading={isPublishLoading}
        css={{
          alignSelf: 'center',
          height: '44px',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          ...(isButtonDisabled ? { pointerEvents: 'none' } : {}),
        }}
      >
        {textLabel}
      </Button>
    );
  };

  const renderCTAButton = () => {
    const renderedButton = renderButton(
      getCTAButtonArgs({
        active,
        isEligibleForRentGuarantee,
        isEnabledForRentGuarantee,
        isFreemiumEnabled,
        isNotActiveAndUpdateActiveStatusNotAllowed,
        isPackageProduct,
        isPaidMultifamily,
        isPublishedDisabled,
        isScreenMobile,
        isSelfServeOnboardingInProgress,
        isUpdateRestricted,
        isWaitlisted,
        shouldAutoActivate,
        adproduct,
        features,
        productType,
        waitlistEligibleProductTypes,
      }),
    );

    return isActiveAndUpdateActiveStatusNotAllowed ? withUnableToDeactivatePopper(renderedButton) : renderedButton;
  };

  return (
    <>
      {/* @ts-expect-error */}
      <div ref={isVisibleRef} />
      <Box
        data-testid="StickyListingFooterContainer"
        css={stickyListingFooterContainerStyles.raw({
          isVisible: !!isVisible,
          isWizardView,
        })}
      >
        <Page.Content css={pageContentStyles}>
          <Box css={flexContainerStyles}>
            {renderPricingContent ? (
              <>
                <Tag
                  tone={getTagTypeByKey(STATUS_KEY.LISTING_SUMMARY, displayStatus)}
                  css={{ marginRight: '200', marginBottom: { base: '150', lg: '0' } }}
                >
                  {displayStatus}
                </Tag>
                <Box css={middleFlexStyles}>
                  <LearnMore housingConnectorStatus={housingConnectorStatus} syndication={syndication} />

                  {showFeedConnectRate ? (
                    <FeedConnectAdditionalInformation hidePrice={!active && !isUpdateActiveStatusAllowed} />
                  ) : (
                    <>
                      <AdditionalInformation />
                      {adproduct.isUnpaid && (
                        <Text textStyle="body-bold" css={{ marginRight: '600' }}>
                          {adproduct.productTypesDisplayText.title}
                        </Text>
                      )}
                    </>
                  )}
                </Box>
              </>
            ) : (
              <ErrorMessage
                isActive={active}
                partialMessages={partialMessages}
                disablePublishIfDuplicatePaidListingPosted={disablePublishIfDuplicatePaidListingPosted}
              />
            )}
          </Box>
          {renderCTAButton()}
        </Page.Content>
      </Box>
      {showWaitlistModal && <WaitlistModal setShowWaitlistModal={setShowWaitlistModal} />}
      {displayDeactivationModal && (
        <DeactivationModal
          setDisplayDeactivateModal={setDisplayDeactivateModal}
          launchedFrom={LAUNCHED_FROM_OPTIONS.EDIT_LISTING_PAGE}
        />
      )}
      {showMultifamilyDeactivationModal && (
        <MultifamilyDeactivationModal setShowMultifamilyDeactivateModal={setShowMultifamilyDeactivationModal} />
      )}
      {showFeedListingDeactivateModal && (
        <FeedListingDeactivateModal
          setShowFeedListingDeactivateModal={setShowFeedListingDeactivateModal}
          launchedFrom={LAUNCHED_FROM_OPTIONS.EDIT_LISTING_PAGE}
        />
      )}
    </>
  );
};

export default ConnectedListingFooter;
